import { useSyncExternalStore } from 'react'
import {
  FILE_DOWNLOAD_ERROR_MESSAGE,
  fileDownloadLimitMessage
} from '../../constants/fileDownloadConstants'

export const MAX_ACTIVITY_DOWNLOADS = 3

let downloads = []
const listeners = new Set()
let sequence = 0
const emit = () => listeners.forEach((listener) => listener())
const update = (id, patch) => {
  const index = downloads.findIndex((item) => item.id === id)
  if (index < 0) return
  const item = downloads[index]
  const updated = { ...item, ...patch }
  // Show recent status changes even when more than three downloads are visible.
  downloads =
    patch.status && patch.status !== item.status
      ? [...downloads.filter((entry) => entry.id !== id), updated]
      : downloads.map((entry) => (entry.id === id ? updated : entry))
  emit()
}
export const useFileDownloads = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => downloads
  )

export function countActiveDownloads(kind) {
  return downloads.filter((item) => item.kind === kind && item.status === 'loading').length
}

export const useActiveDownloadCount = (kind) =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => countActiveDownloads(kind)
  )
export const dismissDownload = (id) => update(id, { visible: false })
export function clearFileDownloads() {
  downloads.forEach((item) => item.controller?.abort())
  downloads = []
  emit()
}
if (typeof window !== 'undefined')
  window.addEventListener('ubiqua-session-cleared', clearFileDownloads)

/**
 * Ejecuta una descarga que persiste al cambiar de pantalla; cada reintento conserva sus parámetros.
 * `task(signal, onProgress)` realiza la descarga y devuelve { filename, message, empty }.
 * `toastMessages` permite personalizar los avisos por estado con objetos o funciones del item.
 * Ejemplo: runFileDownload({ title: 'Archivo CSV', task: downloadCsv,
 *   toastMessages: { success: { title: 'CSV listo', message: 'Descarga completada.' } } })
 */
export function runFileDownload({ title, task, kind, context = {}, toastMessages = {} }) {
  const atLimit = kind === 'activities' && countActiveDownloads(kind) >= MAX_ACTIVITY_DOWNLOADS
  const id = `file-download-${++sequence}`
  const controller = new AbortController()
  const item = {
    id,
    title,
    kind,
    context,
    toastMessages,
    status: atLimit ? 'error' : 'loading',
    ...(atLimit
      ? { message: fileDownloadLimitMessage(MAX_ACTIVITY_DOWNLOADS) }
      : {}),
    visible: true,
    controller,
    retry: () => runFileDownload({ title, task, kind, context, toastMessages })
  }
  downloads = [...downloads, item]
  emit()
  // Retries use this entry point too and must reserve a slot before running.
  if (atLimit) return Promise.resolve()
  return Promise.resolve()
    .then(() => {
      if (controller.signal.aborted) return
      return task(controller.signal, (progress) => {
        if (!controller.signal.aborted) update(id, { progress })
      })
    })
    .then((result = {}) => {
      if (controller.signal.aborted) return
      update(id, { ...result, status: result.empty ? 'empty' : 'success' })
      return result
    })
    .catch((error) => {
      if (controller.signal.aborted || error?.name === 'AbortError') {
        update(id, { visible: false, status: 'cancelled' })
        return
      }
      update(id, {
        status: 'error',
        message: error?.message || FILE_DOWNLOAD_ERROR_MESSAGE
      })
    })
}
